'use client';

import * as React from 'react';

import type { Breakpoint } from '../hooks/use-responsive';

import { Box } from './box';
import { Portal } from './portal';

interface DrawerRootProps {
  open?: boolean;
  onClose?: () => void;
  children: React.ReactNode;
}

interface DrawerContextValue {
  open: boolean;
  onClose: () => void;
}

const DrawerContext = React.createContext<DrawerContextValue | undefined>(undefined);

const useDrawer = () => {
  const context = React.useContext(DrawerContext);
  if (!context) {
    throw new Error('Drawer components must be used within Drawer.Root');
  }
  return context;
};

function DrawerRoot({ open = false, onClose = () => {}, children }: DrawerRootProps) {
  const value = React.useMemo(() => ({ open, onClose }), [open, onClose]);

  // Standard drawer UX: Escape dismisses. Without this, an open drawer
  // only closes via the backdrop click and silently swallows pointer
  // events on the page beneath it.
  React.useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  return <DrawerContext.Provider value={value}>{children}</DrawerContext.Provider>;
}

function DrawerBackdrop() {
  const { open, onClose } = useDrawer();

  if (!open) return null;

  return (
    <Portal>
      <Box
        position="fixed"
        inset={0}
        backgroundColor="rgba(0, 0, 0, 0.5)"
        zIndex="overlay"
        onClick={onClose}
        style={{
          opacity: open ? 1 : 0,
          transition: 'opacity 0.2s ease-in-out',
        }}
      />
    </Portal>
  );
}

function DrawerPositioner({ children }: { children: React.ReactNode }) {
  const { open } = useDrawer();

  if (!open) return null;

  return (
    <Portal>
      <Box
        position="fixed"
        top={0}
        left={0}
        right={0}
        bottom={0}
        zIndex="modal"
        pointerEvents="none"
      >
        {children}
      </Box>
    </Portal>
  );
}

type ResponsiveValue<T> = T | Partial<Record<Breakpoint, T>>;

interface DrawerContentProps {
  children: React.ReactNode;
  placement?: 'left' | 'right' | 'top' | 'bottom';
  maxWidth?: ResponsiveValue<string>;
  maxHeight?: ResponsiveValue<string>;
  width?: ResponsiveValue<string>;
  height?: ResponsiveValue<string>;
}

function DrawerContent({
  children,
  placement = 'left',
  maxWidth,
  maxHeight,
  width = 'auto',
  height = '100%',
}: DrawerContentProps) {
  const { open } = useDrawer();

  const getTransform = () => {
    if (!open) {
      switch (placement) {
        case 'left':
          return 'translateX(-100%)';
        case 'right':
          return 'translateX(100%)';
        case 'top':
          return 'translateY(-100%)';
        case 'bottom':
          return 'translateY(100%)';
        default:
          return 'translateX(-100%)';
      }
    }
    return 'translate(0, 0)';
  };

  const positionStyles = {
    left: { left: 0, top: 0, height: '100%' },
    right: { right: 0, top: 0, height: '100%' },
    top: { top: 0, left: 0, width: '100%' },
    bottom: { bottom: 0, left: 0, width: '100%' },
  };

  return (
    <Box
      position="absolute"
      {...positionStyles[placement]}
      width={width}
      height={height}
      maxWidth={maxWidth}
      maxHeight={maxHeight}
      backgroundColor="white"
      boxShadow="lg"
      pointerEvents="auto"
      style={{
        transform: getTransform(),
        transition: 'transform 0.2s ease-in-out',
      }}
    >
      {children}
    </Box>
  );
}

interface DrawerHeaderProps {
  children: React.ReactNode;
}

function DrawerHeader({ children }: DrawerHeaderProps) {
  return (
    <Box padding={4} borderBottomWidth="1px" borderColor="#E5E7EB">
      {children}
    </Box>
  );
}

interface DrawerBodyProps {
  children: React.ReactNode;
  padding?: number | string;
}

function DrawerBody({ children, padding = 4 }: DrawerBodyProps) {
  return (
    <Box flex={1} overflow="auto" padding={padding}>
      {children}
    </Box>
  );
}

interface DrawerFooterProps {
  children: React.ReactNode;
}

function DrawerFooter({ children }: DrawerFooterProps) {
  return (
    <Box padding={4} borderTopWidth="1px" borderColor="#E5E7EB">
      {children}
    </Box>
  );
}

export const Drawer = {
  Root: DrawerRoot,
  Backdrop: DrawerBackdrop,
  Positioner: DrawerPositioner,
  Content: DrawerContent,
  Header: DrawerHeader,
  Body: DrawerBody,
  Footer: DrawerFooter,
};
